import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { EJE_THEME } from '../../Common/Constant';
import * as request from '../../Common/Util/Request';
import './index.scss';

const PINK = '#F24462';
const PURPLE = '#7B61FF';

const CHART_PERIOD_LABEL = { '7d': '7일', '30d': '30일', month: '이번달', all: '전체' };

const fmt = (d) => d.format('YYYY-MM-DD');
const today = () => dayjs();

function formatWon(n) {
  return (Number(n) || 0).toLocaleString('ko-KR') + '원';
}

function formatMyeong(n) {
  return (Number(n) || 0).toLocaleString('ko-KR') + '명';
}

// 증감률(%) 문자열, 소수 1자리. 비교 불가면 null → 호출부에서 해당 행을 렌더하지 않는다.
// 비교대상이 null/undefined/0 이거나 어느 쪽이든 유한수가 아니면 계산하지 않는다.
// (0으로 나눠 Infinity, 미로딩 값으로 NaN, 전년 데이터 없음을 -100%로 오표기하는 것을 모두 차단)
function growthRate(current, base) {
  // 현재값·비교값 양쪽 모두 가드한다. Number(null) === 0 이고 0은 유한수라
  // null 을 걸러내지 않으면 current=null 이 -100%("▼ 100%")로 오표기된다.
  if (current === null || current === undefined) return null;
  if (base === null || base === undefined) return null;
  const cur = Number(current);
  const b = Number(base);
  // b === 0 검사는 Math.abs(0) === 0 이라 반드시 유지해야 한다. 지우면 0으로 나눠 Infinity 가 나간다.
  if (!Number.isFinite(cur) || !Number.isFinite(b) || b === 0) return null;
  // 분모는 절대값. 순거래액 = 결제 - 취소 라 비교 대상이 음수일 수 있는데(결제 없이 취소만 있는 달),
  // 음수로 나누면 부호가 뒤집혀 개선이 ▼, 악화가 ▲ 로 표시된다. Math.abs 로 증감 방향을 바로잡는다.
  // 양수 base 에서는 Math.abs(b) === b 이므로 기존 결과와 완전히 동일하다.
  return (((cur - b) / Math.abs(b)) * 100).toFixed(1);
}

// 비교 한 블록 = 증감률 줄 + 절대값 줄. 값 유무와 무관하게 항상 2줄을 렌더해 레이아웃을 고정한다.
//
// 행을 통째로 숨기면 ① 실제 0 ② 데이터 없음 ③ 기능 미구현이 화면에서 구분되지 않는다.
// 그래서 퍼센트를 못 내는 경우엔 '—' 로 자리를 지키고, 절대값 줄로 실제 상태를 구분해 보여준다.
//
// base 별 표기:
//   유한수(≠0) → ▲/▼ N%      + `{absLabel} {값}`
//   0          → —            + `{absLabel} 0...`   (실제로 0이었음)
//   null       → —            + `{absLabel} 데이터 없음`
//   undefined  → 렌더 안 함 (미로딩)
//
// 0 과 null 을 같게 표시하면 안 된다. null 은 서비스 시작(2022-09) 이전이라
// 0이었는지 알 수 없는 구간이고, 회계 수치 카드에 0원을 찍으면 없는 사실을 만들어낸다.
function CompareBlock({ current, base, label, absLabel, format }) {
  if (base === undefined) return null; // 미로딩 — 자리도 잡지 않는다
  const rate = growthRate(current, base);
  const isUp = rate !== null && Number(rate) >= 0;
  const growthClass = rate === null ? '' : isUp ? ' is-up' : ' is-down';
  return (
    <div>
      <div className={`dashboard__kpi-growth${growthClass}`}>
        {rate === null ? '—' : `${isUp ? '▲' : '▼'} ${Math.abs(Number(rate))}%`} <span>{label}</span>
      </div>
      <div className="dashboard__kpi-last-month">
        {absLabel} {base === null ? '데이터 없음' : format(base)}
      </div>
    </div>
  );
}

const MAX_RANGE_DAYS = 365;

// 토글 버튼(기본) + 캘린더 아이콘(부가) → 날짜 범위 선택 전환
function PeriodControl({ period, onPeriodChange, customRange, onCustomRange, options = ['7d', '30d', 'month'] }) {
  if (customRange) {
    const [start, end] = customRange;
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="dashboard__period-control">
          <DatePicker
            value={start}
            onChange={(v) => {
              if (!v) return;
              let newEnd = end;
              if (v.isAfter(end)) newEnd = v;
              else if (end.isAfter(v.add(MAX_RANGE_DAYS - 1, 'day'))) newEnd = v.add(MAX_RANGE_DAYS - 1, 'day');
              if (newEnd.isAfter(today())) newEnd = today();
              onCustomRange([v, newEnd]);
            }}
            maxDate={today()}
            format="YY/MM/DD"
            slotProps={{ textField: { size: 'small', className: 'dashboard__period-datepicker' } }}
          />
          <span className="dashboard__period-sep">~</span>
          <DatePicker
            value={end}
            onChange={(v) => {
              if (!v) return;
              let newStart = start;
              if (v.isBefore(start)) newStart = v;
              else if (start.isBefore(v.subtract(MAX_RANGE_DAYS - 1, 'day')))
                newStart = v.subtract(MAX_RANGE_DAYS - 1, 'day');
              onCustomRange([newStart, v.isAfter(today()) ? today() : v]);
            }}
            maxDate={today()}
            format="YY/MM/DD"
            slotProps={{ textField: { size: 'small', className: 'dashboard__period-datepicker' } }}
          />
          <IconButton size="small" className="dashboard__period-close-btn" onClick={() => onCustomRange(null)}>
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </div>
      </LocalizationProvider>
    );
  }

  return (
    <div className="dashboard__period-control">
      <ToggleButtonGroup
        value={period}
        exclusive
        onChange={(_, v) => v && onPeriodChange(v)}
        size="small"
        className="dashboard__period-toggle"
      >
        {options.map((opt) => (
          <ToggleButton key={opt} value={opt}>
            {CHART_PERIOD_LABEL[opt] ?? opt}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <IconButton
        size="small"
        className="dashboard__period-cal-btn"
        onClick={() => onCustomRange([today().subtract(6, 'day'), today()])}
      >
        <CalendarTodayIcon fontSize="inherit" />
      </IconButton>
    </div>
  );
}

function SalesTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const { date } = payload[0].payload;
  return (
    <div className="dashboard__chart-tooltip">
      <div className="dashboard__chart-tooltip-date">{date}</div>
      <div className="dashboard__chart-tooltip-amount">{formatWon(payload[0].value)}</div>
    </div>
  );
}

function UsersTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const { date, count } = payload[0].payload;
  return (
    <div className="dashboard__chart-tooltip">
      <div className="dashboard__chart-tooltip-date">{date}</div>
      <div className="dashboard__chart-tooltip-count">{(Number(count) || 0).toLocaleString('ko-KR')}명</div>
    </div>
  );
}

// 항목 설명 툴팁 — info 아이콘에 마우스 올리면 설명 노출
function InfoTip({ text }) {
  return (
    <MuiTooltip
      title={text}
      arrow
      placement="top"
      enterTouchDelay={0}
      componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
    >
      <InfoOutlinedIcon className="dashboard__info-icon" />
    </MuiTooltip>
  );
}

function KpiCard({ label, value, sub, onClick }) {
  return (
    <Paper
      elevation={0}
      className={`dashboard__kpi-card${onClick ? ' dashboard__kpi-card--clickable' : ''}`}
      onClick={onClick}
    >
      <div className="dashboard__kpi-label">
        {label}
        {onClick && <OpenInNewIcon className="dashboard__shortcut-icon" />}
      </div>
      <div className="dashboard__kpi-value">{value}</div>
      {sub}
    </Paper>
  );
}

function Dashboard() {
  const history = useHistory();
  const admin = useSelector((state) => state.admin);
  const authes = (admin.User_Auths || []).map((ua) => ua.FK_auth.type);
  const hasSalesAuth = authes.includes('sales') || authes.includes('admin');

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);

  const [topLectures, setTopLectures] = useState([]);
  const [topLecturesLoading, setTopLecturesLoading] = useState(true);
  const [topLecturesError, setTopLecturesError] = useState(false);
  const [topN, setTopN] = useState(5);
  const [topNDraft, setTopNDraft] = useState('5');

  const [sales, setSales] = useState(null);
  const [salesLoading, setSalesLoading] = useState(hasSalesAuth);
  const [salesError, setSalesError] = useState(false);

  const [enrollments, setEnrollments] = useState(null);
  const [couponStats, setCouponStats] = useState(null);
  const [paidCouponStats, setPaidCouponStats] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [paidConversionData, setPaidConversionData] = useState(null);

  // 기간 토글 states
  const [newUsersChartPeriod, setNewUsersChartPeriod] = useState('7d');
  const [salesChartPeriod, setSalesChartPeriod] = useState('7d');
  const [enrollmentsChartPeriod, setEnrollmentsChartPeriod] = useState('7d');
  const [topLecturesPeriod, setTopLecturesPeriod] = useState('7d');
  const [couponPeriod, setCouponPeriod] = useState('month');
  const [paidCouponPeriod, setPaidCouponPeriod] = useState('month');

  const [salesTargetMonth, setSalesTargetMonth] = useState(() => dayjs());
  const [todaySalesView, setTodaySalesView] = useState('net');
  const [monthSalesView, setMonthSalesView] = useState('net');
  const [chartSalesView, setChartSalesView] = useState('net');

  // 날짜 직접 지정 states (null = 토글 모드, [dayjs, dayjs] = 커스텀 모드)
  const [usersCustomRange, setUsersCustomRange] = useState(null);
  const [salesCustomRange, setSalesCustomRange] = useState(null);
  const [enrollCustomRange, setEnrollCustomRange] = useState(null);
  const [topCustomRange, setTopCustomRange] = useState(null);
  const [couponCustomRange, setCouponCustomRange] = useState(null);
  const [paidCouponCustomRange, setPaidCouponCustomRange] = useState(null);
  const [conversionPeriod, setConversionPeriod] = useState('month');
  const [convCustomRange, setConvCustomRange] = useState(null);
  const [paidConversionPeriod, setPaidConversionPeriod] = useState('month');
  const [paidConvCustomRange, setPaidConvCustomRange] = useState(null);

  const isInitialSummary = useRef(true);
  const isInitialSales = useRef(true);
  const isInitialEnrollments = useRef(true);
  const isInitialTopLectures = useRef(true);
  const isInitialCoupon = useRef(true);
  const isInitialPaidCoupon = useRef(true);
  const isInitialConversion = useRef(true);
  const isInitialPaidConversion = useRef(true);

  // 초기 전체 로드
  useEffect(() => {
    let mounted = true;

    setSummaryLoading(true);
    request
      .getDashboardSummary({ usersChartPeriod: newUsersChartPeriod, topLecturesPeriod, topN })
      .then((res) => {
        if (!mounted) return;
        const d = res?.data ?? res;
        setSummary(d);
        setTopLectures(d?.topLectures ?? []);
        setSummaryError(false);
      })
      .catch(() => {
        if (mounted) setSummaryError(true);
      })
      .finally(() => {
        if (mounted) {
          setSummaryLoading(false);
          setTopLecturesLoading(false);
        }
        isInitialSummary.current = false;
        isInitialTopLectures.current = false;
      });

    request.getDashboardEnrollments({ enrollmentsChartPeriod }).then((res) => {
      if (mounted) setEnrollments(res?.data ?? res);
      isInitialEnrollments.current = false;
    });

    request.getDashboardCouponStats({ couponPeriod }).then((res) => {
      if (mounted) setCouponStats(res?.data ?? res);
      isInitialCoupon.current = false;
    });

    request.getDashboardCouponStats({ couponPeriod: paidCouponPeriod }).then((res) => {
      if (mounted) setPaidCouponStats(res?.data ?? res);
      isInitialPaidCoupon.current = false;
    });

    request.getDashboardConversionRate({ conversionPeriod }).then((res) => {
      if (mounted) setConversionData(res?.data ?? res);
      isInitialConversion.current = false;
    });

    request.getDashboardConversionRate({ conversionPeriod: paidConversionPeriod }).then((res) => {
      if (mounted) setPaidConversionData(res?.data ?? res);
      isInitialPaidConversion.current = false;
    });

    if (hasSalesAuth) {
      setSalesLoading(true);
      request
        .getDashboardSales({ salesChartPeriod, targetMonth: salesTargetMonth.format('YYYY-MM') })
        .then((res) => {
          if (!mounted) return;
          setSales(res?.data ?? res);
          setSalesError(false);
        })
        .catch(() => {
          if (mounted) setSalesError(true);
        })
        .finally(() => {
          if (mounted) setSalesLoading(false);
          isInitialSales.current = false;
        });
    }

    return () => {
      mounted = false;
    };
  }, []);

  // 신규 가입자 차트 period 또는 커스텀 날짜 변경
  useEffect(() => {
    if (isInitialSummary.current) return;
    let mounted = true;
    setSummaryLoading(true);
    const params = usersCustomRange
      ? { startDate: fmt(usersCustomRange[0]), endDate: fmt(usersCustomRange[1]), topLecturesPeriod, topN }
      : { usersChartPeriod: newUsersChartPeriod, topLecturesPeriod, topN };
    request
      .getDashboardSummary(params)
      .then((res) => {
        if (!mounted) return;
        const d = res?.data ?? res;
        setSummary(d);
        setSummaryError(false);
      })
      .catch(() => {
        if (mounted) setSummaryError(true);
      })
      .finally(() => {
        if (mounted) setSummaryLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [newUsersChartPeriod, usersCustomRange]);

  // 매출 차트 period 또는 커스텀 날짜 변경
  useEffect(() => {
    if (!hasSalesAuth || isInitialSales.current) return;
    let mounted = true;
    setSalesLoading(true);
    const params = salesCustomRange
      ? { startDate: fmt(salesCustomRange[0]), endDate: fmt(salesCustomRange[1]) }
      : { salesChartPeriod };
    params.targetMonth = salesTargetMonth.format('YYYY-MM');
    request
      .getDashboardSales(params)
      .then((res) => {
        if (!mounted) return;
        setSales(res?.data ?? res);
        setSalesError(false);
      })
      .catch(() => {
        if (mounted) setSalesError(true);
      })
      .finally(() => {
        if (mounted) setSalesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [salesChartPeriod, salesCustomRange, salesTargetMonth, hasSalesAuth]);

  // 수강 신청 추이 period 또는 커스텀 날짜 변경
  useEffect(() => {
    if (isInitialEnrollments.current) return;
    let mounted = true;
    const params = enrollCustomRange
      ? { startDate: fmt(enrollCustomRange[0]), endDate: fmt(enrollCustomRange[1]) }
      : { enrollmentsChartPeriod };
    request.getDashboardEnrollments(params).then((res) => {
      if (mounted) setEnrollments(res?.data ?? res);
    });
    return () => {
      mounted = false;
    };
  }, [enrollmentsChartPeriod, enrollCustomRange]);

  // 인기 강의 period / topN / 커스텀 날짜 변경
  useEffect(() => {
    if (isInitialTopLectures.current) return;
    let mounted = true;
    setTopLecturesLoading(true);
    const params = topCustomRange
      ? { startDate: fmt(topCustomRange[0]), endDate: fmt(topCustomRange[1]), topN }
      : { topLecturesPeriod, topN };
    request
      .getDashboardTopLectures(params)
      .then((res) => {
        if (!mounted) return;
        const d = res?.data ?? res;
        setTopLectures(d?.topLectures ?? []);
        setTopLecturesError(false);
      })
      .catch(() => {
        if (mounted) setTopLecturesError(true);
      })
      .finally(() => {
        if (mounted) setTopLecturesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [topLecturesPeriod, topN, topCustomRange]);

  // 전체 쿠폰 사용률 period 또는 커스텀 날짜 변경
  useEffect(() => {
    if (isInitialCoupon.current) return;
    let mounted = true;
    const params = couponCustomRange
      ? { startDate: fmt(couponCustomRange[0]), endDate: fmt(couponCustomRange[1]) }
      : { couponPeriod };
    request.getDashboardCouponStats(params).then((res) => {
      if (mounted) setCouponStats(res?.data ?? res);
    });
    return () => {
      mounted = false;
    };
  }, [couponPeriod, couponCustomRange]);

  // 유료 결제 쿠폰 사용률 period 또는 커스텀 날짜 변경
  useEffect(() => {
    if (isInitialPaidCoupon.current) return;
    let mounted = true;
    const params = paidCouponCustomRange
      ? { startDate: fmt(paidCouponCustomRange[0]), endDate: fmt(paidCouponCustomRange[1]) }
      : { couponPeriod: paidCouponPeriod };
    request.getDashboardCouponStats(params).then((res) => {
      if (mounted) setPaidCouponStats(res?.data ?? res);
    });
    return () => {
      mounted = false;
    };
  }, [paidCouponPeriod, paidCouponCustomRange]);

  // 전체 수강 전환율 period 또는 커스텀 날짜 변경
  useEffect(() => {
    if (isInitialConversion.current) return;
    let mounted = true;
    const params = convCustomRange
      ? { startDate: fmt(convCustomRange[0]), endDate: fmt(convCustomRange[1]) }
      : { conversionPeriod };
    request.getDashboardConversionRate(params).then((res) => {
      if (mounted) setConversionData(res?.data ?? res);
    });
    return () => {
      mounted = false;
    };
  }, [conversionPeriod, convCustomRange]);

  // 유료 결제 수강 전환율 period 또는 커스텀 날짜 변경
  useEffect(() => {
    if (isInitialPaidConversion.current) return;
    let mounted = true;
    const params = paidConvCustomRange
      ? { startDate: fmt(paidConvCustomRange[0]), endDate: fmt(paidConvCustomRange[1]) }
      : { conversionPeriod: paidConversionPeriod };
    request.getDashboardConversionRate(params).then((res) => {
      if (mounted) setPaidConversionData(res?.data ?? res);
    });
    return () => {
      mounted = false;
    };
  }, [paidConversionPeriod, paidConvCustomRange]);

  const pendingReviewCount = summary?.pendingReviewCount ?? 0;

  const monthDisplaySales = monthSalesView === 'gross' ? sales?.monthGrossSales : sales?.monthSales;
  const monthDisplayLastSales = monthSalesView === 'gross' ? sales?.lastMonthGrossSales : sales?.lastMonthSales;
  // 순거래액/거래액 토글 양쪽 대응. 전년 데이터가 없는 구간은 서버가 null을 내려보낸다(0과 구분).
  const monthDisplayLastYearSales = monthSalesView === 'gross' ? sales?.lastYearGrossSales : sales?.lastYearSales;
  // 이번달 누적 가입자 비교 — hasSalesAuth 분기로 KpiCard가 2곳에 중복 렌더되므로
  // sub 노드를 한 번만 만들어 양쪽에 동일하게 넘긴다(한쪽만 수정되는 사고 방지).
  const newUsersSub = (
    <div className="dashboard__kpi-compare">
      <CompareBlock
        current={summary?.monthNewUsers}
        base={summary?.lastMonthNewUsers}
        label="전월 대비"
        absLabel="전월"
        format={formatMyeong}
      />
      <CompareBlock
        current={summary?.monthNewUsers}
        base={summary?.lastYearNewUsers}
        label="전년도 동기간 대비"
        absLabel="전년 동월"
        format={formatMyeong}
      />
    </div>
  );

  const applyTopN = () => {
    const n = parseInt(topNDraft, 10);
    if (!isNaN(n) && n >= 1) {
      const clamped = Math.min(n, 100);
      setTopN(clamped);
      setTopNDraft(String(clamped));
    } else {
      setTopNDraft(String(topN));
    }
  };

  return (
    <ThemeProvider theme={EJE_THEME}>
      <div className="dashboard">
        {/* KPI 카드 */}
        {hasSalesAuth ? (
          <>
            <div className="dashboard__kpi-row">
              {summaryLoading ? (
                <div className="dashboard__loading dashboard__loading--inline">
                  <CircularProgress size={28} sx={{ color: PINK }} />
                </div>
              ) : summaryError ? (
                <Paper elevation={0} className="dashboard__kpi-card dashboard__error">
                  데이터를 불러올 수 없습니다
                </Paper>
              ) : (
                <>
                  <KpiCard
                    label="오늘 신규 가입자"
                    value={`${(summary?.todayNewUsers ?? 0).toLocaleString('ko-KR')}명`}
                    onClick={() => history.push('/user/list')}
                  />
                  <KpiCard
                    label="이번달 누적 가입자"
                    value={`${(summary?.monthNewUsers ?? 0).toLocaleString('ko-KR')}명`}
                    sub={newUsersSub}
                    onClick={() => history.push('/user/list')}
                  />
                </>
              )}
              {salesLoading ? (
                <div className="dashboard__loading dashboard__loading--inline">
                  <CircularProgress size={28} sx={{ color: PINK }} />
                </div>
              ) : salesError ? (
                <Paper elevation={0} className="dashboard__kpi-card dashboard__error">
                  데이터를 불러올 수 없습니다
                </Paper>
              ) : (
                <>
                  <Paper
                    elevation={0}
                    className="dashboard__kpi-card dashboard__kpi-card--clickable"
                    onClick={() => history.push('/sales/old')}
                  >
                    <div className="dashboard__kpi-label">
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        오늘
                        <button
                          className="dashboard__sales-label-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTodaySalesView((v) => (v === 'net' ? 'gross' : 'net'));
                          }}
                          title={todaySalesView === 'net' ? '거래액으로 전환' : '순거래액으로 전환'}
                        >
                          <span className="dashboard__sales-label-btn__text">
                            {todaySalesView === 'net' ? '순거래액' : '거래액'}
                          </span>
                          <span className="dashboard__sales-label-btn__icon">⇅</span>
                        </button>
                        <InfoTip
                          text={
                            '순거래액 = 결제액에서 취소·환불액을 뺀 실질 매출\n거래액 = 취소 반영 전 총 결제액\n\n라벨을 클릭하면 전환됩니다.'
                          }
                        />
                      </span>
                      <OpenInNewIcon className="dashboard__shortcut-icon" />
                    </div>
                    <div className="dashboard__kpi-value">
                      {formatWon(todaySalesView === 'gross' ? sales?.todayGrossSales : sales?.todaySales)}
                    </div>
                  </Paper>
                  <Paper elevation={0} className="dashboard__kpi-card">
                    <div className="dashboard__kpi-label">
                      <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <button
                          className="dashboard__sales-label-btn"
                          onClick={() => setMonthSalesView((v) => (v === 'net' ? 'gross' : 'net'))}
                          title={monthSalesView === 'net' ? '거래액으로 전환' : '순거래액으로 전환'}
                        >
                          <span className="dashboard__sales-label-btn__text">
                            {monthSalesView === 'net' ? '순거래액' : '거래액'}
                          </span>
                          <span className="dashboard__sales-label-btn__icon">⇅</span>
                        </button>
                        <InfoTip
                          text={
                            '순거래액 = 결제액에서 취소·환불액을 뺀 실질 매출\n거래액 = 취소 반영 전 총 결제액\n\n라벨을 클릭하면 전환됩니다.'
                          }
                        />
                      </span>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          views={['year', 'month']}
                          openTo="month"
                          value={salesTargetMonth}
                          onChange={(v) => v && setSalesTargetMonth(v)}
                          minDate={dayjs('2022-09-01')}
                          maxDate={today()}
                          format="YY년 MM월"
                          slotProps={{
                            textField: { size: 'small', className: 'dashboard__month-picker' },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="dashboard__kpi-value">{formatWon(monthDisplaySales)}</div>
                    {/* 절대값 줄은 퍼센트 조건에서 분리돼 있다 — 0 나눗셈과 무관하므로 함께 사라지면 안 된다 */}
                    <div className="dashboard__kpi-compare">
                      <CompareBlock
                        current={monthDisplaySales}
                        base={monthDisplayLastSales}
                        label="전월 대비"
                        absLabel="전월"
                        format={formatWon}
                      />
                      <CompareBlock
                        current={monthDisplaySales}
                        base={monthDisplayLastYearSales}
                        label="전년도 동기간 대비"
                        absLabel="전년 동월"
                        format={formatWon}
                      />
                    </div>
                  </Paper>
                </>
              )}
            </div>
            <div className="dashboard__kpi-row">
              {/* 전체 쿠폰 사용률 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    전체 쿠폰 사용률
                    <InfoTip text="기간 내 전체 결제 건(무료 결제 포함) 중 쿠폰이 적용된 건의 비율" />
                  </span>
                  <PeriodControl
                    period={couponPeriod}
                    onPeriodChange={setCouponPeriod}
                    customRange={couponCustomRange}
                    onCustomRange={setCouponCustomRange}
                    options={['7d', '30d', 'month', 'all']}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {couponStats?.couponUsageRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  결제 {couponStats?.totalTxCount?.toLocaleString() ?? 0}건 중{' '}
                  {couponStats?.couponTxCount?.toLocaleString() ?? 0}건 쿠폰 적용
                </div>
              </Paper>
              {/* 유료 결제 쿠폰 사용률 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    유료 결제 쿠폰 사용률
                    <InfoTip text="실제 결제 금액이 있는 유료 결제 건 중 쿠폰이 적용된 건의 비율 (100% 쿠폰·무료 건 제외)" />
                  </span>
                  <PeriodControl
                    period={paidCouponPeriod}
                    onPeriodChange={setPaidCouponPeriod}
                    customRange={paidCouponCustomRange}
                    onCustomRange={setPaidCouponCustomRange}
                    options={['7d', '30d', 'month', 'all']}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {paidCouponStats?.paidCouponUsageRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  유료 결제 {paidCouponStats?.paidTotalTxCount?.toLocaleString() ?? 0}건 중{' '}
                  {paidCouponStats?.paidCouponTxCount?.toLocaleString() ?? 0}건 쿠폰 적용
                </div>
              </Paper>
              {/* 전체 수강 전환율 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    전체 수강 전환율
                    <InfoTip text="기간 내 신규 가입자 중 수강 신청(무료 포함)을 한 사람의 비율" />
                  </span>
                  <PeriodControl
                    period={conversionPeriod}
                    onPeriodChange={setConversionPeriod}
                    customRange={convCustomRange}
                    onCustomRange={setConvCustomRange}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {conversionData?.conversionRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  신규 가입자 {conversionData?.newUserCount?.toLocaleString() ?? 0}명 중{' '}
                  {conversionData?.enrolledCount?.toLocaleString() ?? 0}명 수강 신청
                </div>
              </Paper>
              {/* 유료 결제 수강 전환율 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    유료 결제 수강 전환율
                    <InfoTip text="기간 내 신규 가입자 중 실제 유료 결제(무료 제외)를 한 사람의 비율" />
                  </span>
                  <PeriodControl
                    period={paidConversionPeriod}
                    onPeriodChange={setPaidConversionPeriod}
                    customRange={paidConvCustomRange}
                    onCustomRange={setPaidConvCustomRange}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {paidConversionData?.paidConversionRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  신규 가입자 {paidConversionData?.newUserCount?.toLocaleString() ?? 0}명 중{' '}
                  {paidConversionData?.paidEnrolledCount?.toLocaleString() ?? 0}명 유료 결제
                </div>
              </Paper>
            </div>
          </>
        ) : (
          <>
            <div className="dashboard__kpi-row">
              {summaryLoading ? (
                <div className="dashboard__loading dashboard__loading--inline">
                  <CircularProgress size={28} sx={{ color: PINK }} />
                </div>
              ) : summaryError ? (
                <Paper elevation={0} className="dashboard__kpi-card dashboard__error">
                  데이터를 불러올 수 없습니다
                </Paper>
              ) : (
                <>
                  <KpiCard
                    label="오늘 신규 가입자"
                    value={`${(summary?.todayNewUsers ?? 0).toLocaleString('ko-KR')}명`}
                    onClick={() => history.push('/user/list')}
                  />
                  <KpiCard
                    label="이번달 누적 가입자"
                    value={`${(summary?.monthNewUsers ?? 0).toLocaleString('ko-KR')}명`}
                    sub={newUsersSub}
                    onClick={() => history.push('/user/list')}
                  />
                </>
              )}
            </div>
            <div className="dashboard__kpi-row">
              {/* 전체 쿠폰 사용률 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    전체 쿠폰 사용률
                    <InfoTip text="기간 내 전체 결제 건(무료 결제 포함) 중 쿠폰이 적용된 건의 비율" />
                  </span>
                  <PeriodControl
                    period={couponPeriod}
                    onPeriodChange={setCouponPeriod}
                    customRange={couponCustomRange}
                    onCustomRange={setCouponCustomRange}
                    options={['7d', '30d', 'month', 'all']}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {couponStats?.couponUsageRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  결제 {couponStats?.totalTxCount?.toLocaleString() ?? 0}건 중{' '}
                  {couponStats?.couponTxCount?.toLocaleString() ?? 0}건 쿠폰 적용
                </div>
              </Paper>
              {/* 유료 결제 쿠폰 사용률 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    유료 결제 쿠폰 사용률
                    <InfoTip text="실제 결제 금액이 있는 유료 결제 건 중 쿠폰이 적용된 건의 비율 (100% 쿠폰·무료 건 제외)" />
                  </span>
                  <PeriodControl
                    period={paidCouponPeriod}
                    onPeriodChange={setPaidCouponPeriod}
                    customRange={paidCouponCustomRange}
                    onCustomRange={setPaidCouponCustomRange}
                    options={['7d', '30d', 'month', 'all']}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {paidCouponStats?.paidCouponUsageRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  유료 결제 {paidCouponStats?.paidTotalTxCount?.toLocaleString() ?? 0}건 중{' '}
                  {paidCouponStats?.paidCouponTxCount?.toLocaleString() ?? 0}건 쿠폰 적용
                </div>
              </Paper>
              {/* 전체 수강 전환율 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    전체 수강 전환율
                    <InfoTip text="기간 내 신규 가입자 중 수강 신청(무료 포함)을 한 사람의 비율" />
                  </span>
                  <PeriodControl
                    period={conversionPeriod}
                    onPeriodChange={setConversionPeriod}
                    customRange={convCustomRange}
                    onCustomRange={setConvCustomRange}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {conversionData?.conversionRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  신규 가입자 {conversionData?.newUserCount?.toLocaleString() ?? 0}명 중{' '}
                  {conversionData?.enrolledCount?.toLocaleString() ?? 0}명 수강 신청
                </div>
              </Paper>
              {/* 유료 결제 수강 전환율 */}
              <Paper elevation={0} className="dashboard__kpi-card">
                <div className="dashboard__kpi-label dashboard__kpi-label--stack">
                  <span className="dashboard__kpi-label-title">
                    유료 결제 수강 전환율
                    <InfoTip text="기간 내 신규 가입자 중 실제 유료 결제(무료 제외)를 한 사람의 비율" />
                  </span>
                  <PeriodControl
                    period={paidConversionPeriod}
                    onPeriodChange={setPaidConversionPeriod}
                    customRange={paidConvCustomRange}
                    onCustomRange={setPaidConvCustomRange}
                  />
                </div>
                <div className="dashboard__kpi-value" style={{ marginTop: 8 }}>
                  {paidConversionData?.paidConversionRate ?? '-'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>
                  신규 가입자 {paidConversionData?.newUserCount?.toLocaleString() ?? 0}명 중{' '}
                  {paidConversionData?.paidEnrolledCount?.toLocaleString() ?? 0}명 유료 결제
                </div>
              </Paper>
            </div>
          </>
        )}

        {/* 행 1: 차트 */}
        <div className="dashboard__main-row">
          {/* 매출 추이 (sales 권한자) */}
          {hasSalesAuth && (
            <Paper elevation={0} className="dashboard__chart-card">
              <div className="dashboard__card-header">
                <div className="dashboard__card-title">
                  <button
                    className="dashboard__sales-label-btn"
                    onClick={() => setChartSalesView((v) => (v === 'net' ? 'gross' : 'net'))}
                    title={chartSalesView === 'net' ? '거래액으로 전환' : '순거래액으로 전환'}
                  >
                    <span className="dashboard__sales-label-btn__text">
                      {chartSalesView === 'net' ? '순거래액' : '거래액'}
                    </span>
                    <span className="dashboard__sales-label-btn__icon">⇅</span>
                  </button>{' '}
                  추이
                </div>
                <div className="dashboard__card-header-right">
                  <PeriodControl
                    period={salesChartPeriod}
                    onPeriodChange={setSalesChartPeriod}
                    customRange={salesCustomRange}
                    onCustomRange={setSalesCustomRange}
                  />
                  <IconButton
                    size="small"
                    className="dashboard__shortcut-btn"
                    onClick={() => history.push('/sales/old')}
                  >
                    <OpenInNewIcon fontSize="inherit" />
                  </IconButton>
                </div>
              </div>
              {salesLoading ? (
                <div className="dashboard__loading">
                  <CircularProgress size={32} sx={{ color: PINK }} />
                </div>
              ) : salesError ? (
                <div className="dashboard__error">데이터를 불러올 수 없습니다</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sales?.salesChart ?? []} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#888' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => (Math.abs(v) >= 10000 ? `${v / 10000}만` : v)}
                    />
                    <Tooltip cursor={{ fill: 'rgba(242, 68, 98, 0.06)' }} content={<SalesTooltip />} />
                    <Bar
                      dataKey={chartSalesView === 'gross' ? 'grossAmount' : 'amount'}
                      fill={PINK}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          )}

          {/* 신규 가입자 */}
          <Paper elevation={0} className="dashboard__chart-card">
            <div className="dashboard__card-header">
              <div className="dashboard__card-title">신규 가입자</div>
              <div className="dashboard__card-header-right">
                <PeriodControl
                  period={newUsersChartPeriod}
                  onPeriodChange={setNewUsersChartPeriod}
                  customRange={usersCustomRange}
                  onCustomRange={setUsersCustomRange}
                />
                <IconButton size="small" className="dashboard__shortcut-btn" onClick={() => history.push('/user/list')}>
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </div>
            </div>
            {summaryLoading ? (
              <div className="dashboard__loading">
                <CircularProgress size={32} sx={{ color: PURPLE }} />
              </div>
            ) : summaryError ? (
              <div className="dashboard__error">데이터를 불러올 수 없습니다</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary?.newUsersChart ?? []} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#888' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip cursor={{ fill: 'rgba(123, 97, 255, 0.06)' }} content={<UsersTooltip />} />
                  <Bar dataKey="count" fill={PURPLE} radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>

          {/* 수강 신청 추이 */}
          <Paper elevation={0} className="dashboard__chart-card">
            <div className="dashboard__card-header">
              <div className="dashboard__card-title">일일 수강 신청 추이</div>
              <div className="dashboard__card-header-right">
                <PeriodControl
                  period={enrollmentsChartPeriod}
                  onPeriodChange={setEnrollmentsChartPeriod}
                  customRange={enrollCustomRange}
                  onCustomRange={setEnrollCustomRange}
                />
                <IconButton
                  size="small"
                  className="dashboard__shortcut-btn"
                  onClick={() => history.push('/lecture/user')}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </div>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={enrollments?.enrollmentsChart || []} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="dashboard__chart-tooltip">
                        <div className="dashboard__chart-tooltip-date">{label}</div>
                        {payload.map((p) => (
                          <div key={p.dataKey} style={{ color: p.stroke, fontSize: '0.875rem', fontWeight: 700 }}>
                            {p.dataKey === 'enrollCount' ? '수강 신청' : '취소'}: {p.value.toLocaleString()}건
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="enrollCount"
                  stroke="#00a76f"
                  strokeWidth={2}
                  dot={false}
                  name="수강 신청"
                />
                <Line
                  type="monotone"
                  dataKey="refundCount"
                  stroke="#f24462"
                  strokeWidth={2}
                  dot={false}
                  name="취소"
                  strokeDasharray="4 2"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </div>

        {/* 행 2: 처리 필요 */}
        <Paper elevation={0} className="dashboard__alert-card">
          <div className="dashboard__card-title">처리 필요</div>
          {summaryLoading ? (
            <div className="dashboard__loading">
              <CircularProgress size={24} sx={{ color: PINK }} />
            </div>
          ) : summaryError ? (
            <div className="dashboard__error">데이터를 불러올 수 없습니다</div>
          ) : (
            <div
              className="dashboard__alert-item dashboard__alert-item--clickable"
              onClick={() => history.push('/review/lecture')}
            >
              <span className="dashboard__alert-label">승인 대기 리뷰</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="dashboard__alert-badge">{pendingReviewCount}건</span>
                <OpenInNewIcon className="dashboard__shortcut-icon" />
              </div>
            </div>
          )}
        </Paper>

        {/* 행 3: 인기 강의 */}
        <div className="dashboard__main-row">
          <Paper elevation={0} className="dashboard__top-lectures-card">
            <div className="dashboard__top-lectures-header">
              <div className="dashboard__card-header">
                <div className="dashboard__card-title">인기 강의 TOP {topN}</div>
                <IconButton
                  size="small"
                  className="dashboard__shortcut-btn"
                  onClick={() => history.push('/lecture/user')}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </div>
              <div className="dashboard__period-bar">
                <PeriodControl
                  period={topLecturesPeriod}
                  onPeriodChange={setTopLecturesPeriod}
                  customRange={topCustomRange}
                  onCustomRange={setTopCustomRange}
                  options={['7d', '30d', 'month', 'all']}
                />
                <TextField
                  type="number"
                  size="small"
                  value={topNDraft}
                  onChange={(e) => setTopNDraft(e.target.value)}
                  onBlur={applyTopN}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyTopN();
                      e.target.blur();
                    }
                  }}
                  inputProps={{ min: 1, max: 100 }}
                  className="dashboard__period-custom"
                />
                <span className="dashboard__period-custom-unit">개</span>
              </div>
            </div>
            {topLecturesLoading ? (
              <div className="dashboard__loading">
                <CircularProgress size={28} sx={{ color: PINK }} />
              </div>
            ) : topLecturesError ? (
              <div className="dashboard__error">데이터를 불러올 수 없습니다</div>
            ) : (
              <div style={topN > 5 ? { maxHeight: 245, overflowY: 'auto', borderRadius: 4 } : undefined}>
                <Table size="small" className="dashboard__top-lectures-table" stickyHeader={topN > 5}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" width={40}>
                        순위
                      </TableCell>
                      <TableCell align="left" width="auto">
                        강의명
                      </TableCell>
                      <TableCell align="center" width={72}>
                        종류
                      </TableCell>
                      <TableCell align="right" width={60}>
                        수강생
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topLectures.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#aaa', fontSize: '0.8125rem' }}>
                          데이터가 없습니다.
                        </TableCell>
                      </TableRow>
                    ) : (
                      topLectures.map((lecture, idx) => (
                        <TableRow
                          key={lecture.id ?? idx}
                          hover
                          style={{ cursor: 'pointer' }}
                          onClick={() => history.push(`/lecture/single/new/${lecture.id}`)}
                        >
                          <TableCell align="center">
                            <span className={`dashboard__rank dashboard__rank--${idx + 1}`}>{idx + 1}</span>
                          </TableCell>
                          <TableCell align="left" className="dashboard__lecture-title">
                            {lecture.title}
                          </TableCell>
                          <TableCell align="center">{lecture.listenType ?? '-'}</TableCell>
                          <TableCell align="right">{(lecture.studentCount ?? 0).toLocaleString('ko-KR')}명</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </Paper>
        </div>
      </div>
    </ThemeProvider>
  );
}

export default Dashboard;
